// react and material ui imports
import React, { useEffect, useState } from 'react'
import { Box, Icon, InputAdornment, Skeleton, Typography } from '@mui/material'

// services and hooks
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  getAllCandidate,
  getTalentQuota,
  removeTalent,
} from 'services/api/modules/recruitment/external-talent'
import { useDebouncedEffect } from 'services/hooks/useDebouncedEffect'
import { retryProtectedQuery } from 'services/session/queryPolicy'

// components
import DashboardContainer from 'components/smart/dashboard-container/DashboardContainer'
import AdxInput from 'components/custom/AdxInput'
import AdxEmptyState from 'components/reusable/emptyState/AdxEmptyState'
import ExternalTalentTable from '../components/table/ExternalTalentTable'
import ImportTalentCard from '../components/ImportTalentCard'
import SampleDataList from '../components/SampleDataList'
import ViewTalentDrawer from '../components/ViewTalentDrawer'
import ResumeParsingStatus from '../components/ResumeParsingStatus'
import ImportTalentModal from '../components/ImportTalentModal'
import UploadHistoryDrawer from '../components/UploadHistoryDrawer'
import EDProgressBar from 'module/performance/pages/evaluation-details/components/EDProgressBar'

// icons
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward'
import CircleIcon from '@mui/icons-material/Circle'
import AdxEmptyStateV2 from 'components/reusable/emptyState/AdxEmptyStateV2'
import AdxSortTable from 'components/reusable/table/AdxSortTable'

const QUERY_KEYS = {
  CANDIDATES: 'getAllCandidate',
  TALENT_QUOTA: 'getTalentQuota',
}

const ExternalTalent = () => {
  const queryClient = useQueryClient()
  const [talentId, setTalentId] = useState(null)
  const [search, setSearch] = useState('')
  const [searchItem, setSearchItem] = useState('')
  const [drawerState, setDrawerState] = useState(false)

  const handleDrawer = (anchor, open) => () => {
    setDrawerState({ ...drawerState, [anchor]: open })
  }

  const handleClearSearch = () => {
    setSearchItem('')
  }

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const [initialLoading, setInitialLoading] = useState(true)

  //api call
  const { data, isLoading, error } = useQuery(
    [QUERY_KEYS.CANDIDATES, rowsPerPage, page, search],
    async () => {
      const { data } = await getAllCandidate(rowsPerPage, page + 1, search)
      if (search !== '' && page >= data?.candidates?.last_page) {
        setPage(0)
      }
      return data
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
      retry: (failureCount, queryError) => retryProtectedQuery(failureCount, queryError, 2),
    }
  )

  const { data: talentQuotaData, isLoading: talentQuotaIsLoading } = useQuery(
    [QUERY_KEYS.TALENT_QUOTA],
    async () => {
      const { data } = await getTalentQuota()
      return data
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 300000,
      cacheTime: 3600000,
      retry: (failureCount, queryError) => retryProtectedQuery(failureCount, queryError, 2),
    }
  )

  const progressBarData = (talentQuotaData?.quota_used / talentQuotaData?.total_quota) * 100

  const refreshQueries = () => {
    // Only invalidate when necessary
    queryClient.invalidateQueries(QUERY_KEYS.CANDIDATES, {
      refetchActive: true,
      refetchInactive: false,
    })

    // Check if quota needs refresh
    if (queryClient.getQueryData(QUERY_KEYS.TALENT_QUOTA)) {
      queryClient.invalidateQueries(QUERY_KEYS.TALENT_QUOTA, {
        refetchActive: true,
        refetchInactive: false,
      })
    }
  }

  // set delay for search api
  useDebouncedEffect(
    () => {
      if (searchItem.length > 0) {
        setSearch(`&search=${searchItem}`)
      } else {
        setSearch('')
      }
    },
    [searchItem],
    700
  )

  const { mutate: removeCandidate } = useMutation(removeTalent, {
    onSuccess: () => {
      queryClient.invalidateQueries('getAllCandidate')
    },
    onError: (error) => {
      console.error(error)
    },
  })

  const handleRemove = () => {
    const data = {
      candidate_id: [talentId],
    }
    removeCandidate(data)
  }

  const talentColumn = [
    {
      Header: 'Talent',
      accessor: 'name',
      Cell: (props) => {
        const { value } = props
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <Box
              sx={{
                width: '35px',
                height: '35px',
                backgroundColor: 'default-background.main',
                borderRadius: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Icon
                sx={{ width: '20px', height: '20px', fontSize: '20px', color: 'alternate.main' }}
              >
                person_outline_outlined
              </Icon>
            </Box>
            <Box>
              <Typography noWrap>
                {value !== '' ? value : `Talent ${props?.row?.original?.id}`}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ color: 'light-gray-text.main' }}>
                  {`${props.row?.original?.id}-${props.row?.original?.relations?.resume?.batch}`}
                </Typography>
                <CircleIcon sx={{ fontSize: '5px', color: '#8D9593', mx: '8px' }} />
                <Typography variant="subtitle2" sx={{ color: 'light-gray-text.main' }}>
                  Uploaded on {props?.row?.original?.relations.resume.uploaded_at}{' '}
                </Typography>
              </Box>
            </Box>
          </Box>
        )
      },
    },
    {
      Header: 'Email',
      accessor: 'email',
      Cell: (props) => {
        const { value } = props
        return (
          <Typography sx={{ color: 'light-gray-text.main' }}>
            {value !== '' ? value : '-'}
          </Typography>
        )
      },
    },
    // {
    //   Header: 'Profile score',
    //   accessor: 'profile_score',
    //   Cell: (props) => {
    //     const { value } = props
    //     return (
    //       <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
    //         <Icon sx={{ color: '#F3BF3A', fontSize: '16px' }}>stars_rounded</Icon>
    //         <Typography>{value ?? '--'}</Typography>
    //       </Box>
    //     )
    //   },
    // },px:'12px',py:'8px'
    {
      id: 4,
      Header: 'Matched job',
      accessor: 'matched_jobs',
      Cell: (props) => {
        const { value, row } = props

        return (
          <Box
            sx={{
              display: 'flex',
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                px: '12px',
                py: '8px',
                backgroundColor: value.length > 1 ? 'gray-inactive-button.main' : '',
                borderRadius: '100px',
                justifyContent: 'center',
                overflow: 'hidden',

                width: value.length > 1 ? '75%' : '100%',
                mr: value.length > 1 ? '8px' : 0,
              }}
            >
              <Typography
                sx={{ color: 'light-gray-text.main', whiteSpace: 'nowrap', overflow: 'hidden' }}
              >
                {value[0]?.job_advertisement ?? '-'}
              </Typography>
            </Box>
            {value.length > 1 ? (
              <Box
                sx={{
                  display: 'flex',
                  px: '12px',
                  py: '8px',
                  backgroundColor: 'gray-inactive-button.main',
                  borderRadius: '100px',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                <Typography
                  sx={{ color: 'light-gray-text.main', whiteSpace: 'nowrap', overflow: 'hidden' }}
                >
                  {`+ ${value.length - 1}`}
                </Typography>
              </Box>
            ) : null}
          </Box>
        )
      },
    },
  ]

  useEffect(() => {
    setInitialLoading(false)
  }, [])

  return (
    <DashboardContainer childrenMaxWidth headerTitle="Talent Upload" blockMobile>
      {initialLoading && data?.candidates?.data?.length === 0 && searchItem.length > 0 ? (
        <>
          <ImportTalentCard />
          <SampleDataList />
        </>
      ) : (
        <>
          <Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'start',
                  justifyContent: 'space-between',
                  width: 1,
                  px: 3,
                  py: 2,
                  color: 'white.main',
                  background: 'linear-gradient(to right, #EF6617, #F3BF3A, #F27C19)',
                  height: 110,
                }}
              >
                <Typography>External talents ({data?.candidates?.total})</Typography>
                <Box
                  onClick={handleDrawer('right', true)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    cursor: 'pointer',
                  }}
                >
                  <Typography noWrap sx={{ textDecoration: 'underline' }}>
                    Upload log
                  </Typography>
                  <Icon sx={{ fontSize: '20px' }}>history</Icon>
                </Box>
              </Box>
              <Box sx={{ mt: -6.5, width: '70%' }}>
                <UploadQuotaCard
                  data={talentQuotaData}
                  isLoading={talentQuotaIsLoading}
                  progressBarData={progressBarData}
                />
              </Box>
            </Box>
          </Box>
          <Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 3 }}>
              {initialLoading ? (
                <Skeleton variant="rounded" width="100%" height="67px" />
              ) : data?.redundant_count || data?.is_parsing ? (
                <ResumeParsingStatus
                  isParsing={data?.is_parsing}
                  redundantCount={data?.redundant_count}
                  refreshQueries={refreshQueries}
                />
              ) : null}
              <Box
                sx={{
                  border: 1,
                  borderColor: 'gray-line.main',
                  borderRadius: '4.5px',
                  bgcolor: 'white.main',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {initialLoading ? (
                  <Skeleton variant="rounded" width="100%" height="67px" />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      p: 2,
                      borderBottom: 1,
                      borderColor: 'gray-line.main',
                    }}
                  >
                    <AdxInput
                      placeholder="Search name"
                      value={searchItem}
                      onChange={(e) => setSearchItem(e.target.value)}
                      sx={{ width: '300px' }}
                      startAdornment={
                        <InputAdornment position="start">
                          <Icon>search</Icon>
                        </InputAdornment>
                      }
                      endAdornment={
                        searchItem ? (
                          <InputAdornment position="end">
                            <Icon
                              onClick={handleClearSearch}
                              sx={{ color: 'error.main', cursor: 'pointer' }}
                            >
                              close
                            </Icon>
                          </InputAdornment>
                        ) : null
                      }
                    />
                    <ImportTalentModal
                      isParsing={data?.is_parsing}
                      refreshQueries={refreshQueries}
                      progressBarData={progressBarData}
                      remainingQuota={talentQuotaData?.quota_remaining}
                    />
                  </Box>
                )}
                {initialLoading ? (
                  <Skeleton variant="rounded" width="100%" height="67px" />
                ) : (
                  <>
                    {/* if search data is empty ( no candidate) */}
                    {(error !== null || data?.candidates?.data?.length === 0) &&
                    searchItem.length > 0 ? (
                      <AdxEmptyStateV2
                        icon={<Icon>question_mark</Icon>}
                        title="Candidate does not exist"
                      />
                    ) : (
                      <>
                        <ExternalTalentTable
                          column={talentColumn}
                          data={data?.candidates?.data || []}
                          nav={<ViewTalentDrawer handleRemove={handleRemove} talentId={talentId} />}
                          onClick={(e) => setTalentId(e.id)}
                          count={data?.candidates?.total}
                          page={page}
                          onPageChange={handleChangePage}
                          rowsPerPage={rowsPerPage}
                          onRowsPerPageChange={handleChangeRowsPerPage}
                          pagination
                          isLoading={isLoading}
                          height={'calc(100vh - 440px)'}
                          stickyHeader
                        />
                      </>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </>
      )}
      <UploadHistoryDrawer initialTab={1} drawerState={drawerState} handleDrawer={handleDrawer} />
    </DashboardContainer>
  )
}

export default ExternalTalent

export const UploadQuotaCard = ({ data, isLoading, progressBarData }) => {
  const formatDate = (dateString) => {
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' }
    return new Date(dateString).toLocaleDateString('en-GB', options)
  }

  const formattedPeriod = formatDate(data?.period_end)

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          border: 1,
          borderColor: 'gray-line.main',
          borderRadius: '4.5px',
          backgroundColor: 'white.main',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '400px',
            borderRight: 1,
            borderRightColor: 'gray-line.main',
            padding: '20px',
            gap: '20px',
          }}
        >
          <Box
            sx={{
              backgroundColor: 'default-background.main',
              flexGrow: 1,
              padding: '14px',
              borderRadius: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <ArrowOutwardIcon sx={{ color: 'positive.main', height: '28px', width: '28px' }} />
          </Box>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <Skeleton variant="text" width={'40%'} />
            <Skeleton variant="text" />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: '20px',
            width: '100%',
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: 'dark-text.main',
            }}
          >
            <Skeleton variant="text" width={'20%'} />
            <Skeleton variant="text" width={'15%'} />
          </Box>
          <Skeleton variant="text" width={'100%'} height={'8px'} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Skeleton variant="text" width={'30%'} />
            <Skeleton variant="text" width={'25%'} />
          </Box>
        </Box>
      </Box>
    )
  } else {
    return (
      <Box
        sx={{
          display: 'flex',
          border: 1,
          borderColor: 'gray-line.main',
          borderRadius: '4.5px',
          backgroundColor: 'white.main',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '400px',
            borderRight: 1,
            borderRightColor: 'gray-line.main',
            padding: '20px',
            gap: '20px',
          }}
        >
          <Box
            sx={{
              backgroundColor: 'default-background.main',
              flexGrow: 1,
              padding: '14px',
              borderRadius: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <ArrowOutwardIcon sx={{ color: 'positive.main', height: '28px', width: '28px' }} />
          </Box>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <Typography variant="h3">{data?.quota_remaining}</Typography>
            <Typography sx={{ color: 'light-gray-text.main' }}>Remaining uploads</Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: '20px',
            width: '100%',
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: 'dark-text.main',
            }}
          >
            <Typography variant="subtitle1">Monthly quota</Typography>
            <Typography variant="subtitle1">{`${data?.quota_used} of ${data?.total_quota}`}</Typography>
          </Box>
          <EDProgressBar
            progress={progressBarData}
            sx={{ width: '100%', height: '8px', borderRadius: '5px' }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            {progressBarData > 0 && (
              <Typography
                sx={{ color: 'light-gray-text.main' }}
              >{`${data?.total_resume_candidate} (Talent uploads) • ${data?.total_resume_job_board} (Job board)`}</Typography>
            )}
            <Typography
              sx={{ color: 'light-gray-text.main' }}
            >{`Renew on ${formattedPeriod}`}</Typography>
          </Box>
        </Box>
      </Box>
    )
  }
}
